import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import type { ReleaseNoteCategory, ReleaseNoteEntry } from "@a7y/types";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { ReleaseNoteItemRow } from "./ReleaseNoteItemRow";
import { formatEntryDate } from "./utils";

interface WeekDetailProps {
  entries: ReleaseNoteEntry[];
  isLoading: boolean;
  queryKey: readonly unknown[];
}

/** Transient state: for each entry, is a draft row open? */
type DraftMap = Record<string, boolean>;

const DEFAULT_DRAFT_CATEGORY: ReleaseNoteCategory = "IMPROVEMENT";

export function WeekDetail({ entries, isLoading, queryKey }: WeekDetailProps) {
  const [drafts, setDrafts] = useState<DraftMap>({});
  // Track busy state per entry so sibling row buttons disable mid-mutation.
  const [busyCounts, setBusyCounts] = useState<Record<string, number>>({});

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [entries],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="h-40 animate-pulse rounded-xl bg-zinc-100 dark:bg-zinc-800"
          />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
        No release-note documents landed in this week.
      </div>
    );
  }

  const openDraft = (entryId: string) =>
    setDrafts((prev) => ({ ...prev, [entryId]: true }));
  const closeDraft = (entryId: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });

  const setRowBusy = (entryId: string) => (busy: boolean) => {
    setBusyCounts((prev) => {
      const current = prev[entryId] ?? 0;
      const next = busy ? current + 1 : Math.max(0, current - 1);
      return { ...prev, [entryId]: next };
    });
  };

  return (
    <div className="space-y-6">
      {sortedEntries.map((entry) => {
        const draftOpen = !!drafts[entry.id];
        const entryBusy = (busyCounts[entry.id] ?? 0) > 0;
        return (
          <section
            key={entry.id}
            className="rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            <header className="flex items-start justify-between gap-4 border-b border-zinc-200 px-5 py-4 dark:border-zinc-700">
              <div>
                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                  {formatEntryDate(entry.date)}
                </h3>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  {entry.id}
                  {" \u00B7 "}
                  Version {entry.version}
                  {" \u00B7 "}
                  {entry.items.length}{" "}
                  {entry.items.length === 1 ? "item" : "items"}
                </p>
              </div>
              <Button
                outline
                onClick={() => openDraft(entry.id)}
                disabled={draftOpen || entryBusy}
              >
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </header>

            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-36">Category</TableHeader>
                  <TableHeader>Title</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader className="w-28">Visibility</TableHeader>
                  <TableHeader className="text-right">Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {entry.items.length === 0 && !draftOpen && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-6 text-center text-sm text-zinc-500 dark:text-zinc-400"
                    >
                      No items. Use &ldquo;Add Item&rdquo; to create the first
                      one.
                    </TableCell>
                  </TableRow>
                )}
                {entry.items.map((item, index) => (
                  <ReleaseNoteItemRow
                    key={index}
                    entryId={entry.id}
                    index={index}
                    item={item}
                    itemCount={entry.items.length}
                    isBusy={entryBusy}
                    onBusyChange={setRowBusy(entry.id)}
                    queryKey={queryKey}
                  />
                ))}
                {draftOpen && (
                  <ReleaseNoteItemRow
                    key={`__draft_${entry.id}`}
                    entryId={entry.id}
                    index={entry.items.length}
                    itemCount={entry.items.length + 1}
                    isDraft
                    item={{
                      category: DEFAULT_DRAFT_CATEGORY,
                      title: "",
                      userFacingTitle: "",
                      description: "",
                      userFacing: true,
                    }}
                    isBusy={entryBusy}
                    onBusyChange={setRowBusy(entry.id)}
                    onCancelDraft={() => closeDraft(entry.id)}
                    queryKey={queryKey}
                  />
                )}
              </TableBody>
            </Table>
          </section>
        );
      })}
    </div>
  );
}
